// Preparing the card WeChat sends when the player taps ··· → 发送给朋友, through WeChat's JS-SDK
// (docs/plans/2026-10-10-wechat-friend-invitations.md, §5 steps 3–6). A page can't open WeChat's
// share menu itself; it only sets the card's content. When anything fails, WeChat sends its plain
// card (the tab title, the address, a generic picture): nothing is shown to the player.
import api from '@/api/api';
import store from '@/store';
import { settings } from './wechat';

// The address the app started at. On iPhones WeChat checks the signature against it, not the current
// one, for single-page apps; Android checks the current one (inferred from developers' reports, not in
// WeChat's documentation; the test site's logs on 2026-10-13 showed iPhones refusing the current one).
const startUrl = window.location.href.split('#')[0];
const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
const LOGO = '/share-logo.png';
// ?wxdebug=1 on the address the app started at: WeChat shows its own alerts for every JS-SDK call.
const debug = /[?&]wxdebug=1(&|$)/.test(window.location.search);

// WeChat's answers are reported to the server's log (POST /api/debug/log), so that a card WeChat
// refuses can be traced; never the signature, and only the kind of page.
function report(what, kind, result) {
  const errMsg = result && (result.errMsg || result.message) ? (result.errMsg || result.message) : String(result);
  Promise.resolve().then(() => api.log(`wechat-share ${what} (${kind}): ${errMsg}`)).catch(() => {});
}

let sdk = null;
let current = 0;
// The first wx.config of this page load. WeChat's script answers only that one through wx.ready: once
// it has answered, wx.ready runs at once, and a refusal of a later configuration reaches the one
// wx.error handler (jweixin 1.6 keeps one state per page load).
let first = null;
let errorKind = 'page';
let errorUrl = '';

function loadSdk() {
  if (!sdk) {
    sdk = new Promise((resolve, reject) => {
      if (window.wx) { resolve(window.wx); return; }
      const s = document.createElement('script');
      s.src = settings.jsSdkUrl;
      s.onload = () => (window.wx ? resolve(window.wx) : reject(new Error('no wx')));
      s.onerror = () => { sdk = null; reject(new Error('JS-SDK not loaded')); };
      document.head.appendChild(s);
    });
  }
  return sdk;
}

let generation = 0;
const applicableUrl = () => ios ? startUrl : window.location.href.split('#')[0];
let configuredUrl = '';
let configuredPromise = null;
function configure(wx, kind) {
  const url = applicableUrl();
  if (configuredUrl === url && configuredPromise) return configuredPromise;
  configuredUrl = url;
  const pending = configureOnce(wx, kind, url, ++generation);
  configuredPromise = pending;
  const clear = () => { if (configuredPromise === pending) { configuredUrl = ''; configuredPromise = null; } };
  pending.then(valid => { if (!valid) clear(); }, clear);
  return pending;
}

async function configureOnce(wx, kind, url, request) {
  const sig = await api.call('POST', 'wechat/signatures', { url });
  // The old page may finish fetching after a newer page already configured this shared SDK.
  if (request !== generation || url !== applicableUrl()) return false;
  errorKind = kind;
  errorUrl = url === startUrl ? 'start' : 'current';
  if (!first) {
    first = new Promise((resolve, reject) => {
      wx.ready(resolve);
      wx.error(e => { report(`config error, ${errorUrl} address`, errorKind, e); reject(e); });
    });
  }
  wx.config({
    debug,
    appId: sig.appId,
    timestamp: sig.timestamp,
    nonceStr: sig.nonceStr,
    signature: sig.signature,
    jsApiList: ['updateAppMessageShareData'],
    openTagList: ['wx-open-subscribe'],
  });
  // A later configuration (Android, after moving within the app) can't be awaited: WeChat runs the
  // calls in the order they are made (inferred).
  await first;
  return request === generation && url === applicableUrl();
}

// Sets the tab title and, for a signed-in player, the card: { title, desc, path }.
export async function prepareCard({ title, desc, path }) {
  const kind = path.split('/')[1] || 'page';
  const n = ++current;
  document.title = title;
  if (!settings.jsSdkUrl || !store.state.user) return;
  try {
    const wx = await loadSdk();
    const valid = await configure(wx, kind);
    if (!valid || n !== current) return;
    const origin = window.location.origin;
    await new Promise(resolve => wx.updateAppMessageShareData({
      title, desc, link: origin + path, imgUrl: origin + LOGO,
      success: r => { report('card set', kind, r); resolve(); },
      fail: r => { report('card refused', kind, r); resolve(); },
    }));
  } catch (e) {
    // WeChat's plain card remains (a refused configuration was reported above).
    if (!(e && e.errMsg)) report('failed', kind, e);
  }
}

export async function prepareSubscription() {
  const wx = await loadSdk();
  if (!await configure(wx, 'subscription')) throw new Error('obsolete SDK configuration');
  return wx;
}
